/**
 * Formats of the column definitions: the patterns ',##0', ",##0'%'",
 * '###.000', '0.000', 'percent' and 'year', or Intl number format options.
 */
export type ColumnFormat = string | Intl.NumberFormatOptions;

/**
 * A column definition: field, caption, data type, format and the display
 * options of a grid column.
 */
export interface ColumnDefinition {
  dataField?: string;
  name?: string;
  caption?: string;
  dataType?: 'string' | 'number' | 'date' | 'datetime' | 'boolean' | 'object';
  format?: ColumnFormat;
  /** Name of the cell template. */
  cellTemplate?: string;
  width?: number | string;
  alignment?: 'left' | 'right' | 'center';
  visible?: boolean;
  showInColumnChooser?: boolean;
  allowSorting?: boolean;
  allowFiltering?: boolean;
}

/** Column description for the app tables, derived from a column definition. */
export interface TableColumn {
  /** Stable identifier, the data field or the cell template name. */
  key: string;
  field: string | null;
  header: string;
  dataType: string;
  format?: ColumnFormat;
  template?: string;
  width?: number;
  /** Numbers are right aligned. */
  alignment: 'left' | 'right' | 'center';
  visible: boolean;
  showInColumnChooser: boolean;
  sortable: boolean;
}

/**
 * The caption of a column without a caption, made from its field name:
 * underscores and dots become spaces, and a lower case letter followed by a
 * capital or a digit ends a word ("lastEmployeeCount" → "Last Employee Count").
 */
export const captionize = (name: string): string =>
  name
    .replace(/[_.]/g, ' ')
    .replace(/(\p{Ll})(?=[\p{Lu}\d])/gu, '$1 ')
    .replace(/^./, (first) => first.toUpperCase());

export const toTableColumns = (columns: ColumnDefinition[]): TableColumn[] =>
  columns.map((column) => {
    const field = column.dataField || null;
    const template = column.cellTemplate;
    return {
      key: field || template || column.name || '',
      field,
      header:
        column.caption !== undefined
          ? column.caption
          : field
          ? captionize(field)
          : '',
      dataType: column.dataType || 'string',
      format: column.format,
      template,
      width: typeof column.width === 'number' ? column.width : undefined,
      alignment:
        column.alignment ||
        (column.dataType === 'number'
          ? 'right'
          : column.dataType === 'boolean'
          ? 'center'
          : 'left'),
      visible: column.visible !== false,
      showInColumnChooser: column.showInColumnChooser !== false,
      sortable: !!field && column.allowSorting !== false,
    };
  });

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    // A plain ISO date is a local date, not UTC midnight.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const date = dateOnly
      ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3])
      : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  return null;
};

/**
 * Formats a cell value for the formats used in the column definitions:
 * `year` and the short date for dates, `###.000`, `0.000`, `,##0`, `,##0'%'`,
 * `percent` and Intl number format options for numbers, plain text otherwise.
 */
export const formatCellValue = (
  value: unknown,
  column: Pick<TableColumn, 'dataType' | 'format'>,
  locale = 'en-GB'
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const { dataType, format } = column;
  if (dataType === 'date' || dataType === 'datetime') {
    const date = toDate(value);
    if (!date) {
      return String(value);
    }
    if (format === 'year') {
      return String(date.getFullYear());
    }
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(date);
  }
  // A column with numbers is a number column without dataType, too.
  if (typeof value === 'number' && (dataType === 'number' || format)) {
    if (format === '###.000' || format === '0.000') {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
        useGrouping: false,
      }).format(value);
    }
    if (format === ",##0'%'") {
      return (
        new Intl.NumberFormat(locale, {
          maximumFractionDigits: 0,
          useGrouping: true,
        }).format(value) + '%'
      );
    }
    if (format === ',##0') {
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(value);
    }
    if (format === 'percent') {
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 0,
      }).format(value);
    }
    if (format && typeof format === 'object') {
      return new Intl.NumberFormat(
        locale,
        format as Intl.NumberFormatOptions
      ).format(value);
    }
    return String(value);
  }
  return String(value);
};
